import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PasDetail, PasRow } from '@fleet/shared';
import { api } from '../../lib/apiClient';

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ['personen', 'passen', 'dashboard', 'cars']) {
    qc.invalidateQueries({ queryKey: [key] });
  }
}

/** Active passes (archived excluded). */
export function usePassen() {
  return useQuery({
    queryKey: ['passen'],
    queryFn: () => api.get<{ passen: PasRow[] }>('/passen').then((r) => r.passen),
  });
}

export function usePasDetail(id: string) {
  return useQuery({
    queryKey: ['passen', id],
    queryFn: () => api.get<PasDetail>(`/passen/${encodeURIComponent(id)}`),
  });
}

export function useCreatePas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pasnummer: string) => api.post('/passen', { pasnummer }),
    onSuccess: () => invalidateAll(qc),
  });
}

/** Only archived (out-of-use) passes, for the passen-archief page. */
export function useArchivedPassen() {
  return useQuery({
    queryKey: ['passen', 'archief'],
    queryFn: () =>
      api
        .get<{ passen: PasRow[] }>('/passen?includeArchived=true')
        .then((r) => r.passen.filter((p) => !p.actief)),
  });
}

export function useArchiveerPas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardId: string) => api.post(`/passen/${encodeURIComponent(cardId)}/archiveer`),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDearchiveerPas() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cardId: string) => api.post(`/passen/${encodeURIComponent(cardId)}/dearchiveer`),
    onSuccess: () => invalidateAll(qc),
  });
}
